import { useCallback, useEffect, useState } from 'react';
import { AppLabelsStorage, type AppLabelsDto, type AppLabelsStorageInterface } from '../storage/AppLabelsStorage';
import { DEFAULT_LABELS, type LabelKey } from './defaultLabels';

export const ENGLISH_LANGUAGE_ID = '1';

/**
 * Label fallback chain: selected language -> English record -> built-in English default -> the
 * key itself. `records` maps languageTypeId -> its (possibly partial) labels.
 */
export const resolveLabel = (
  key: LabelKey,
  languageTypeId: string,
  records: Record<string, Record<string, string> | undefined>
): string =>
  records[languageTypeId]?.[key]
  ?? records[ENGLISH_LANGUAGE_ID]?.[key]
  ?? DEFAULT_LABELS[key]
  ?? key;

// languageTypeId -> labels. Module-level so every component shares one load per language.
// `null` marks "looked up, nothing stored" so a missing language isn't re-read on every render.
const labelCache = new Map<string, Record<string, string> | null>();

const loadLabels = async (storage: AppLabelsStorageInterface, languageTypeId: string): Promise<void> => {
  const dto: AppLabelsDto | null = await new AppLabelsStorage(storage).getAppLabels(languageTypeId);
  labelCache.set(languageTypeId, dto?.labels ?? null);
};

/** Test/diagnostic hook: forget everything loaded so far (e.g. after the bundle re-unpacks). */
export const clearLabelCache = (): void => labelCache.clear();

/**
 * Returns `t(key)` for the given language. Answers from the built-in defaults until the
 * language's stored labels have loaded, so nothing renders blank, then re-renders once with
 * the translated text.
 */
export const useLabels = (
  storage: AppLabelsStorageInterface | null | undefined,
  languageTypeId: string
): { t: (key: LabelKey) => string } => {
  const language = String(languageTypeId || ENGLISH_LANGUAGE_ID);
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!storage) {
      return;
    }
    let cancelled = false;
    const wanted = language === ENGLISH_LANGUAGE_ID ? [language] : [ENGLISH_LANGUAGE_ID, language];
    const missing = wanted.filter(id => !labelCache.has(id));
    if (missing.length === 0) {
      return;
    }
    void Promise.all(missing.map(id => loadLabels(storage, id))).then(() => {
      if (!cancelled) {
        setVersion(v => v + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storage, language]);

  const t = useCallback(
    (key: LabelKey): string =>
      resolveLabel(key, language, {
        [ENGLISH_LANGUAGE_ID]: labelCache.get(ENGLISH_LANGUAGE_ID) ?? undefined,
        [language]: labelCache.get(language) ?? undefined,
      }),
    // labelCache is a mutable module cache; `language` + the version bump drive re-evaluation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, labelCache.get(language), labelCache.get(ENGLISH_LANGUAGE_ID)]
  );

  return { t };
};
